import { useMemo } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";

import { threadHasStarted } from "../components/ChatView.logic";
import { useComposerDraftStore } from "../composerDraftStore";
import type { DraftId } from "../draftId";
import { createThreadSelectorAcrossEnvironments } from "../storeSelectors";
import { useStore } from "../store";
import { resolveDraftCanonicalThreadRef } from "./-draftChatThreadRoute.logic";

/**
 * Which thread a draft route is actually looking at.
 *
 * A draft carries a `threadId` from the moment it exists, but that thread is
 * not necessarily real yet, and once the draft is promoted the canonical
 * reference moves. Three related answers — the draft's own ref, whether its
 * server thread has started, and the canonical ref to navigate to — were
 * derived inline in the route component and made up most of its branching.
 *
 * The `-` prefix keeps TanStack's file-based routing from treating this as a
 * route: in that directory a dot is a path separator and a non-route file is a
 * build warning.
 */
export function useDraftThreadIdentity(draftId: DraftId) {
  const draftSession = useComposerDraftStore((store) => store.getDraftSession(draftId));
  const serverThread = useStore(
    useMemo(
      () => createThreadSelectorAcrossEnvironments(draftSession?.threadId ?? null),
      [draftSession?.threadId],
    ),
  );
  const serverThreadStarted = threadHasStarted(serverThread);

  const canonicalThreadRef = useMemo(
    () =>
      resolveDraftCanonicalThreadRef({
        promotedTo: draftSession?.promotedTo,
        serverThreadStarted,
        serverThreadRef: serverThread
          ? { environmentId: serverThread.environmentId, threadId: serverThread.id }
          : null,
      }),
    [draftSession?.promotedTo, serverThread, serverThreadStarted],
  );

  const threadRef = useMemo(
    () => (draftSession ? scopeThreadRef(draftSession.environmentId, draftSession.threadId) : null),
    [draftSession],
  );

  return { draftSession, serverThread, serverThreadStarted, canonicalThreadRef, threadRef };
}
